import React, { useCallback, useEffect, useState } from 'react'
import { isNil, includes, isEmpty, filter, concat, anyPass, __ } from 'ramda'
import { makeStyles } from '@material-ui/styles'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  EVERYONE_NAME,
  EVERY_GROUP_NAME,
  WITHOUT_GROUP_NAME,
  GROUP,
  SUB_GROUP,
  SUBSCRIBERS,
  ALL_GROUP_TYPE,
} from 'app/constants/recipient'
import { useRecipients } from './useRecipients'
import { AccessType } from 'app/auth/accessControlService'
import { useAccessControl } from 'app/auth/useAccessControl'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import CheckBox from 'app/componentV4/CheckBox'
import Chip from '@material-ui/core/Chip'
import clsx from 'clsx'
import { CircularProgress, useMediaQuery } from '@material-ui/core'
import { SCOPE_BEACON, SCOPE_GENERAL } from 'app/constants/scope'
import {
  checkEquals,
  checkIncludesAll,
  removeArrayAFromArrayB,
} from 'libs/globalFunc'
import useFeatureFlag from 'app/configs/useFeatureFlag'
import CancelIcon from '@material-ui/icons/Cancel'
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined'
import ChannelIcon from 'app/componentV4/Channel/ChannelIcon'
import RestrictedLabel from 'app/components/labels/RestrictedLabel'
import PersonAvatar from 'app/components/PersonAvatar'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import LimitTooltip from 'app/componentV4/Tooltip/LimitTooltip'
import { useTheme } from '@material-ui/core/styles'

const useStyle = makeStyles((theme) => ({
  root: {
    '& .MuiAutocomplete-inputRoot': {
      overflowX: 'auto',
      height: 'auto',
      maxHeight: '130px',
      minHeight: '48px',
      position: 'unset',
      padding: '5px 14px 5px 14px !important',
    },
  },
  rootChip: {
    '& .MuiChip-root': {
      margin: '.25rem',
      backgroundColor: '#0073b1',
      color: '#FFFFFF',
      height: 'auto',
    },
    '.MuiAutocomplete-listbox': {
      maxHeight: '55vh !important',
    },
    '& .MuiChip-deleteIcon': {
      color: '#FFFFFF',
    },
    '& .MuiChip-label': {
      padding: '7px 18px',
    },
  },
  rootChipFlag: {
    '& .MuiChip-root': {
      margin: '.25rem',
      backgroundColor: '#183560',
      color: '#FFFFFF',
      height: 'auto',
    },
    '& .MuiChip-deleteIcon': {
      color: '#ffffff',
    },
    '& .MuiChip-label': {
      padding: '7px 18px',
    },
  },
  rootChipNewVersion: {
    '& .MuiChip-root': {
      margin: '.25rem',
      backgroundColor: '#ffffff',
      color: '#5D7290',
      height: 'auto',
      border: '1px solid #5D7290',
    },
    '& .MuiChip-deleteIcon': {
      color: '#5D7290',
    },
    '& .MuiChip-label': {
      padding: '7px 7px',
    },
  },
  subGroup: {
    textAlign: 'center',
    color: '#89f1ff',
  },
  textSubScribersCount: { color: theme.palette.primary.subtitle },
  avatarStyle: {
    width: '25px',
    height: '25px',
    fontSize: '13px',
  },
  optionSubScriberSubTitle: {
    padding: '10px 5px',
    width: '100%',
  },
  subScriberEmail: {
    color: '#a8a8a8',
    fontSize: '12px',
  },
  inputTextError: {
    '& fieldset': {
      borderRadius: '8px',
    },
    '& .MuiOutlinedInput-input': {
      color: 'red',
    },
    '& .MuiInputBase-input': {
      color: 'red',
    },
  },
  listbox: {
    maxHeight: '55vh',
    overflowY: 'auto',
  },
}))

export default function RecipientsSelector({
  value,
  onChange,
  label,
  helperText,
  error,
  isBeacon,
  valueFromAnotherPage,
  isLoadingCount,
}) {
  const { mes_3832_new_design_compose_message } = useFeatureFlag()
  const [open, setOpen] = useState(false)
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    state: {
      options,
      isEmptyGroupAndSubscriber,
      isLoading,
      searching,
      searchOptions,
      searchText,
    },
    getOptions,
    onSearchRecipient,
    setSearchText,
    checkEveryone,
    mapNewValue,
  } = useRecipients()

  const everyoneOption = options.find((group) => group.type === EVERYONE_NAME)

  const selected = concat(
    filter((option) => includes(option.id, value.subGroups), options),
    value.subscribers,
  )

  const isOpenOptions = open && !isEmptyGroupAndSubscriber
  const { formatMessage } = useIntl()
  const { isGranted: isAdmin } = useAccessControl([AccessType.ADMINISTRATION])
  const classes = useStyle()

  const handleSelected = (option) => {
    let newSubGroups = value.subGroups
    let newSubscribers = value.subscribers

    const everyoneId = options
      .filter(
        (item) =>
          !includes(item.type, [EVERY_GROUP_NAME, GROUP, EVERYONE_NAME]),
      )
      .map((item) => item.id)
    if (option.type === EVERY_GROUP_NAME) {
      const allOptionIds = options
        .filter(
          (item) =>
            !includes(item.type, [
              EVERY_GROUP_NAME,
              GROUP,
              EVERYONE_NAME,
              WITHOUT_GROUP_NAME,
            ]),
        )
        .map((item) => item.id)
      newSubGroups = Array.from(new Set([...allOptionIds, ...newSubGroups]))
    } else if (option.type === GROUP) {
      const allSubGroupByGroupIds = options
        .filter((item) => option.id === item.parentId)
        .map((item) => item.id)

      newSubGroups = newSubGroups.filter(
        (item) => !allSubGroupByGroupIds.includes(item),
      )

      newSubGroups.push(...allSubGroupByGroupIds)
    } else {
      if (option.type === 'subscribers') {
        newSubscribers.push(option)
      } else {
        newSubGroups.push(option.id)
      }
    }
    const everyone = options.find((group) => group.type === EVERYONE_NAME)
    const isEveryone =
      isAdmin &&
      (checkEquals(everyoneId, newSubGroups) || option.type === EVERYONE_NAME)

    onChange({
      ...value,
      subGroups: isEveryone ? [everyone.id] : newSubGroups,
      subscribers: newSubscribers,
    })
  }

  const handleRemoved = (option) => {
    let newValueIds = value.subGroups
    let newSubscribers = value.subscribers

    if (option.type === 'subscribers') {
      newSubscribers = newSubscribers.filter((item) => item.id !== option.id)
    } else {
      const everyone = options.find((group) => group.type === EVERYONE_NAME)

      const isEveryone = isAdmin && includes(everyone.id, value.subGroups)
      const allOptionIds = options
        .filter(
          (item) =>
            !includes(item.type, [EVERY_GROUP_NAME, GROUP, EVERYONE_NAME]),
        )
        .map((item) => item.id)
      if (option.type === EVERYONE_NAME) {
        newValueIds = []
      } else if (option.type === EVERY_GROUP_NAME) {
        if (isEveryone) {
          newValueIds = options
            .filter(
              (item) =>
                !includes(item.type, [
                  EVERYONE_NAME,
                  EVERY_GROUP_NAME,
                  GROUP,
                  SUB_GROUP,
                ]),
            )
            .map((item) => item.id)
        } else {
          const allSubGroupId = options
            .filter(
              (item) =>
                !includes(item.type, [
                  EVERY_GROUP_NAME,
                  GROUP,
                  EVERYONE_NAME,
                  WITHOUT_GROUP_NAME,
                ]),
            )
            .map((item) => item.id)
          newValueIds = value.subGroups.filter(
            (item) => ![...allSubGroupId, option.id].includes(item),
          )
        }
      } else if (option.type === GROUP) {
        const currentValue =
          isEveryone && isAdmin ? allOptionIds : value.subGroups
        const subGroupIdsByGroupId = options
          .filter((item) => item.parentId === option.id)
          .map((item) => item.id)
        newValueIds = currentValue.filter(
          (item) => ![...subGroupIdsByGroupId, option.id].includes(item),
        )
      } else {
        const currentValue =
          isEveryone && isAdmin ? allOptionIds : value.subGroups
        newValueIds = currentValue.filter((item) => item !== option.id)
      }
    }

    onChange({
      ...value,
      subGroups: newValueIds,
      subscribers: newSubscribers,
    })
  }

  const handleOnChangeRecipient = (
    event,
    selectedOptions,
    reason,
    { option },
  ) => {
    event.stopPropagation()
    if (!isEmptyGroupAndSubscriber) {
      let haveValue = false
      if (option.type === SUBSCRIBERS) {
        const subscriberIds = value.subscribers.map(
          (subscriber) => subscriber.id,
        )
        haveValue = includes(option.id, subscriberIds)
      } else {
        const everyone = options.find((option) => option.type === EVERYONE_NAME)
        if (everyone && includes(everyone.id, value.subGroups)) {
          haveValue = true
        } else if (option.type === EVERY_GROUP_NAME) {
          const allSubGroupIds = options
            .filter((item) => item.type === SUB_GROUP)
            .map((item) => item.id)

          haveValue = checkIncludesAll(allSubGroupIds, value.subGroups)
        } else if (option.type === GROUP) {
          const allTags = options
            .filter((item) => item.parentId === option.id)
            .map((item) => item.id)
          haveValue = allTags.every((item) => value.subGroups.includes(item))
        } else {
          haveValue = includes(option.id, value.subGroups)
        }
      }

      if (!haveValue) handleSelected(option)
      if (haveValue) handleRemoved(option)
    }
  }

  const renderChip = (option) => {
    return (
      <div
        className={
          mes_3832_new_design_compose_message
            ? classes.rootChipNewVersion
            : classes.rootChipFlag
        }
        key={`${option.type}-${option.id}`}
      >
        <Chip
          disabled={isLoading}
          label={
            <div>
              {isNil(option?.parentName) ||
              includes(option.type, [EVERY_GROUP_NAME, WITHOUT_GROUP_NAME]) ? (
                isDownSm ? (
                  <LimitTooltip
                    maxWidth={200}
                    title={option.name}
                    className="truncate"
                  />
                ) : (
                  <div>{option.name}</div>
                )
              ) : (
                <>
                  <div>{option.parentName}</div>
                  <div className={classes.subGroup}>{option.name}</div>
                </>
              )}
            </div>
          }
          onDelete={() => {
            handleRemoved(option)
          }}
          deleteIcon={
            mes_3832_new_design_compose_message ? (
              <CloseOutlinedIcon
                fontSize="small"
                style={{
                  pointerEvents: isLoadingCount ? 'none' : 'auto',
                  opacity: isLoadingCount ? 0.3 : 1,
                }}
              />
            ) : (
              <CancelIcon />
            )
          }
        />
      </div>
    )
  }

  const handleRenderInput = (values) => {
    if (isEmpty(values)) return

    const valueIds = values.map((item) => item.id)
    const hasEveryoneTag = values.some((group) => group.type === EVERYONE_NAME)
    const isEveryoneSelected =
      isAdmin && (checkEveryone(valueIds) || hasEveryoneTag)

    const allSubGroupIds = options
      .filter((item) => item.type === SUB_GROUP)
      .map((item) => item.id)

    const isAllGroupsSelected = checkIncludesAll(
      allSubGroupIds,
      value.subGroups,
    )

    let mappedValues

    if (isEveryoneSelected) {
      mappedValues = mapNewValue([everyoneOption], value)
    } else if (isAllGroupsSelected) {
      const everyGroupOption = options.find(
        (group) => group.type === EVERY_GROUP_NAME,
      )

      const removeSubgroup = removeArrayAFromArrayB(
        allSubGroupIds,
        value.subGroups,
      )

      const mapNewOption = options.filter((option) =>
        includes(option.id, removeSubgroup),
      )

      mappedValues = mapNewValue([everyGroupOption, ...mapNewOption], value)
    } else {
      mappedValues = mapNewValue(values, value)
    }

    return mappedValues.map((option) => (
      <div key={option.id}>{renderChip(option)}</div>
    ))
  }

  const handleRenderOption = useCallback(
    (option, { selected }) => {
      const hasEveryone = value.subGroups.includes(everyoneOption?.id)
      const isEveryone = checkEveryone(value.subGroups) || hasEveryone
      const isBold =
        option.type === EVERYONE_NAME ||
        option.type === GROUP ||
        (!isAdmin &&
          [EVERY_GROUP_NAME, WITHOUT_GROUP_NAME].includes(option.type))

      if (!isEmpty(searchText)) {
        const subscriberIds = value.subscribers.map(
          (subscribers) => subscribers.id,
        )

        const isChecked = anyPass([
          (option) => includes(option.id, subscriberIds),
          (option) => includes(option.id, value.subGroups),
          (option) => isEveryone && option.type === 'subGroup',
        ])

        return (
          <div className={clsx(isBold && 'font-bold -ml-3 p-0', 'flex py-8')}>
            <CheckBox checked={isChecked(option)} />
            {option.type === SUBSCRIBERS && (
              <div className="px-4 relative mr-5">
                <PersonAvatar
                  placeHolder={option.avatarCharacters}
                  subId={option.id}
                  alt={option.name}
                  src={option.avatar}
                />
                <div
                  className="flex absolute bottom-0 border border-gray-400 rounded-full"
                  style={{ right: '-3px' }}
                >
                  <ChannelIcon channel={Number(option.channel)} />
                </div>
              </div>
            )}
            <div className="my-auto">
              <div className="flex my-auto space-x-4 items-center">
                <div>
                  {isDownSm ? (
                    <LimitTooltip
                      maxWidth={150}
                      title={option.name}
                      className="truncate"
                    />
                  ) : (
                    <div>{option.name}</div>
                  )}
                </div>
              </div>
              <div>
                {parseInt(option.hideContacts) === 1 ? (
                  <RestrictedLabel />
                ) : (
                  <div className="text-gray-400 text-12">
                    {isDownSm ? (
                      <LimitTooltip
                        maxWidth={150}
                        title={option.email}
                        className="truncate"
                      />
                    ) : (
                      <div className="text-gray-400 text-12">
                        {option.email}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      const allSubGroupIds = options
        .filter((item) => item.type === SUB_GROUP)
        .map((item) => item.id)
      const isAllGroupSelected = checkEquals(allSubGroupIds, value.subGroups)

      const isGroup = option.type === GROUP

      const isAllSubGroupsSelected = isGroup
        ? options
            .filter((item) => item.parentId === option.id)
            .every((sub) => value.subGroups.includes(sub.id))
        : false

      const checked =
        (isAllGroupSelected &&
          includes(option.type, [GROUP, SUB_GROUP, EVERY_GROUP_NAME])) ||
        isAllSubGroupsSelected ||
        isEveryone ||
        selected

      return Number(option.subscribersCount) > 0 ? (
        <div
          className={clsx(
            isBold || option.type === ALL_GROUP_TYPE
              ? 'font-bold -ml-3 p-0'
              : 'ml-5',
            'flex items-baseline ',
          )}
        >
          <CheckBox checked={checked} />
          <div className="flex my-auto">
            <div>{option.name}</div>
            <span
              className={clsx(classes.textSubScribersCount, 'text-xs ml-1')}
            >
              <FormattedMessage
                id="createNotification.dialog.recipient.subscribers.count"
                values={{ count: option.subscribersCount }}
              />
            </span>
          </div>
        </div>
      ) : null
    },
    [options, value.subGroups, value.subscribers, isAdmin, searchText],
  )

  const onSearch = (event, inputValue, reason) => {
    if (reason === 'reset' || isNil(event)) return
    setSearchText(event.target.value)
    onSearchRecipient(
      event.target.value,
      isBeacon ? SCOPE_BEACON : SCOPE_GENERAL,
    )
  }

  useEffect(() => {
    getOptions({ isBeacon })
  }, [isBeacon])

  useEffect(() => {
    if (!isNil(valueFromAnotherPage)) {
      onChange({
        subscribers: valueFromAnotherPage?.subscribers || [],
        subGroups: valueFromAnotherPage?.subGroups || [],
      })
    }
  }, [])

  return (
    <>
      <Autocomplete
        multiple
        options={isEmpty(searchText) ? options : searchOptions}
        filterOptions={(options) => options}
        open={isOpenOptions}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false)
          setSearchText('')
        }}
        disableCloseOnSelect={true}
        groupBy={(option) => option.groupBy}
        getOptionLabel={(option) => option.name}
        value={selected}
        inputValue={searchText}
        loading={isLoading || searching}
        disabled={isLoading}
        renderTags={(opntion) => handleRenderInput(opntion)}
        renderInput={(params) => (
          <>
            {isEmptyGroupAndSubscriber ? (
              <TextField
                id="outlined-basic"
                variant="outlined"
                disabled={true}
                value={formatMessage({ id: 'empty.subscriber.desc' })}
                className={clsx(classes.inputTextError, 'w-full')}
              />
            ) : (
              <TextField
                {...params}
                className="w-full"
                placeholder={
                  value.subGroups.length === 0 && value.subscribers.length === 0
                    ? label
                    : ''
                }
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  endAdornment: (
                    <React.Fragment>
                      {isLoading && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                    </React.Fragment>
                  ),
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace') {
                    event.stopPropagation()
                  }
                }}
              />
            )}
          </>
        )}
        onInputChange={onSearch}
        onChange={handleOnChangeRecipient}
        renderOption={handleRenderOption}
        classes={{
          listbox: classes.listbox,
        }}
        className={clsx(
          classes.root,
          error
            ? 'border border-red bg-white rounded-8'
            : 'bg-white rounded-8 border border-grey-input',
        )}
      />
      {error && (
        <div className="flex space-x-2 py-2">
          <div className="flex my-auto">
            <ErrorOutlineIcon className="text-red-500" fontSize="small" />
          </div>
          <label className="text-red">{helperText}</label>
        </div>
      )}
    </>
  )
}
